import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Send, Sparkles, Bot, User, FileText, Brain, Calendar, Paperclip, File,
    GripVertical, Globe, RefreshCw, ChevronDown, ChevronUp, ExternalLink,
    MessageSquare, Plus, Trash2, Loader2, BookOpen, Loader, Upload, AlertTriangle, Save, Lightbulb,
    PanelLeftClose, PanelLeftOpen, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import CourseEditor from './CourseEditor';
import AIFileUpload from '../Tasks/AIFileUpload';
import AdminCoursePreviewModal from './AdminCoursePreviewModal';

// Web Search Results Display Component - Collapsible cards with View More
const WebSearchResultsDisplay = ({ results }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    if (!results || results.length === 0) return null;

    return (
        <div className="mt-3 pt-3 border-t border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-slate-400">Web Sources</span>
            </div>
            <div className="space-y-2">
                {results.map((result, index) => (
                    <div
                        key={index}
                        className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden"
                    >
                        <div className="px-3 py-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <a
                                        href={result.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-400 hover:text-blue-300 truncate block"
                                    >
                                        {result.title}
                                    </a>
                                    <span className="text-xs text-slate-500">{result.domain}</span>
                                </div>
                                <a
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-slate-500 hover:text-blue-400 transition-colors flex-shrink-0"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </div>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                {result.snippet}
                            </p>
                        </div>
                        {result.fullContent && (
                            <>
                                {expandedIndex === index && (
                                    <div className="px-3 pb-2">
                                        <div className="text-xs text-slate-300 bg-slate-900/50 p-2 rounded max-h-40 overflow-y-auto">
                                            {result.fullContent}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-300 bg-slate-800/60 hover:bg-slate-700/50 transition-colors"
                                >
                                    {expandedIndex === index ? (
                                        <>
                                            <ChevronUp className="w-3 h-3" />
                                            <span>Show Less</span>
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-3 h-3" />
                                            <span>View More</span>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AICourseGenerator = ({ isOpen, onClose, courseType = 'onboarding', onSuccess, initialCourseData = null }) => {
    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // Mobile State
    const [mobileActivePanel, setMobileActivePanel] = useState('chat');

    // Collapsed State
    const [isChatCollapsed, setIsChatCollapsed] = useState(false);
    const toggleChatCollapsed = () => setIsChatCollapsed(!isChatCollapsed);

    // Split Panel State
    const [splitRatio, setSplitRatio] = useState(() => {
        const saved = localStorage.getItem('ai_course_generator_split_ratio');
        return saved ? parseFloat(saved) : 40;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);
    const MIN_CHAT_WIDTH = 300;
    const MIN_EDITOR_WIDTH = 400;

    // Streaming State
    const [streamingMessage, setStreamingMessage] = useState('');
    const [streamingPhase, setStreamingPhase] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    // Suggestions
    const [suggestions, setSuggestions] = useState([]);

    // AI File Upload State
    const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
    const [fileProcessingMessage, setFileProcessingMessage] = useState(null);
    // Files awaiting user intent choice (attach vs analyze)
    const [pendingIntentFiles, setPendingIntentFiles] = useState([]);
    const aiFileInputRef = useRef(null);


    // Course editor state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        duration_days: 30,
        enrollment_method: 'manual',
        auto_enrollment_rules: {
            matching_logic: 'any',
            lifecycle_stages: [],
            roles: [],
            company_wide: false
        }
    });
    const [activeStep, setActiveStep] = useState('details');
    const [isSaving, setIsSaving] = useState(false);

    // Timeline items from AI
    const [timelineItems, setTimelineItems] = useState([]);

    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const initialStateRef = useRef(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    // Check if editor has content (not blank)
    const hasEditorContent = () => {
        return (
            (formData.name && formData.name.trim() !== '') ||
            (formData.description && formData.description.trim() !== '') ||
            timelineItems.length > 0 ||
            formData.duration_days !== 30 ||
            formData.enrollment_method !== 'manual'
        );
    };

    // Detect changes to form data or timeline
    useEffect(() => {
        if (isOpen && initialStateRef.current !== null) {
            const currentState = JSON.stringify({ formData, timelineItems, messages: messages.length });
            const hasChanges = currentState !== initialStateRef.current;
            setHasUnsavedChanges(hasChanges && hasEditorContent());
        }
    }, [formData, timelineItems, messages, isOpen]);



    // Handle browser back button - show unsaved changes modal
    useEffect(() => {
        if (!isOpen) return;

        const handlePopState = () => {
            if (isOpen && hasUnsavedChanges && hasEditorContent() && !showUnsavedModal) {
                // Prevent actual navigation by pushing state back
                window.history.pushState(null, '', window.location.href);
                // Show the unsaved changes modal
                setShowUnsavedModal(true);
            }
        };

        // Add a history entry when the modal opens to intercept back button
        window.history.pushState(null, '', window.location.href);

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, hasUnsavedChanges, showUnsavedModal]);

    // Initialize session and welcome message
    useEffect(() => {
        if (isOpen) {
            if (initialCourseData) {
                // Edit Mode: Load existing course data
                const initializeEditMode = async () => {
                    console.log('Initializing Edit Mode with:', initialCourseData);
                    setSessionId(`course_session_${Date.now()}`); // New session for the edit interaction

                    // Pre-fill form data from passed object (basic info)
                    setFormData({
                        name: initialCourseData.name || '',
                        description: initialCourseData.description || '',
                        duration_days: initialCourseData.duration_days || 30,
                        enrollment_method: initialCourseData.auto_enrollment_enabled ? 'automatic' : 'manual',
                        auto_enrollment_rules: initialCourseData.auto_enrollment_rules || {
                            matching_logic: 'any',
                            lifecycle_stages: [],
                            roles: [],
                            company_wide: false
                        },
                        courseType: initialCourseData.course_type || courseType
                    });

                    // Fetch full details (timeline items)
                    try {
                        const token = localStorage.getItem('token');
                        const response = await api.get(`/courses/${initialCourseData.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        console.log('Fetched Course Details:', response.data);

                        if (response.data.course) {
                            const course = response.data.course;

                            // Restore Chat History from fetched data (more reliable than props)
                            if (course.chat_history && Array.isArray(course.chat_history) && course.chat_history.length > 0) {
                                setMessages(course.chat_history);
                            } else {
                                setMessages([{
                                    id: 1,
                                    role: 'assistant',
                                    content: `I've loaded **${course.name}**. You can ask me to modify the timeline, add new tasks, or rewrite the description.`,
                                    timestamp: new Date()
                                }]);
                            }

                            if (course.items) {
                                const mappedItems = course.items.map(item => ({
                                    id: item.id || Date.now() + Math.random(),
                                    trigger_day: item.relative_day,
                                    item_type: item.item_type,
                                    name: item.title || item.task?.title || item.notice?.title || item.survey?.title || 'Untitled',
                                    title: item.title || item.task?.title || item.notice?.title || item.survey?.title || 'Untitled',
                                    description: item.description || item.task?.description || '',
                                    item_id: item.item_id,
                                    assignment_placeholders: item.assignment_placeholders || [],
                                    task: item.task,
                                    notice: item.notice,
                                    survey: item.survey
                                }));
                                setTimelineItems(mappedItems);
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching course details:', error);
                        // Fallback to initial data if fetch fails
                        setMessages([{
                            id: 1,
                            role: 'assistant',
                            content: `I've loaded **${initialCourseData.name}**. You can ask me to modify the timeline, add new tasks, or rewrite the description.`,
                            timestamp: new Date()
                        }]);
                    }

                    setSuggestions([
                        'Add a welcome task on day 1',
                        'Extend duration to 45 days',
                        'Add a quiz about company values',
                        'Update the course description'
                    ]);

                    // Set initial state reference for edit mode (after data is loaded)
                    setTimeout(() => {
                        initialStateRef.current = JSON.stringify({
                            formData: {
                                name: initialCourseData.name || '',
                                description: initialCourseData.description || '',
                                duration_days: initialCourseData.duration_days || 30,
                                enrollment_method: initialCourseData.auto_enrollment_enabled ? 'automatic' : 'manual',
                                auto_enrollment_rules: initialCourseData.auto_enrollment_rules || {},
                                courseType: initialCourseData.course_type || courseType
                            },
                            timelineItems: [], // Will be populated by fetch
                            messages: 1
                        });
                        setHasUnsavedChanges(false);
                    }, 500);
                };
                initializeEditMode();
            } else {
                // New Course Mode
                const newSessionId = `course_session_${Date.now()}`;
                setSessionId(newSessionId);

                setMessages([{
                    id: 1,
                    role: 'assistant',
                    content: `👋 Hello! I'm your AI Course Assistant. I can help you create **${courseType}** courses for your employees.\n\nTell me what kind of course you'd like to create. For example:\n- "Create a 30-day onboarding program for engineering"\n- "Build a preboarding course with paperwork and welcome tasks"\n- "Set up auto-enrollment for new hires in sales"`,
                    timestamp: new Date()
                }]);

                setSuggestions([
                    `Create a 30-day ${courseType} program`,
                    'Set up auto-enrollment for new hires',
                    'Build a role-specific training course',
                    'Help me choose the right settings'
                ]);

                // Reset state
                setAiUploadedFiles([]);
                setPendingIntentFiles([]);
                setIsFileUploading(false);
                setFileUploadProgress(0);
                setFileProcessingMessage(null);
                setFormData({
                    name: '',
                    description: '',
                    duration_days: 30,
                    enrollment_method: 'manual',
                    auto_enrollment_rules: {
                        matching_logic: 'any',
                        lifecycle_stages: [],
                        roles: [],
                        company_wide: false
                    },
                    courseType: courseType
                });
                setTimelineItems([]);

                // Set initial state reference after a short delay to capture initial state
                setTimeout(() => {
                    initialStateRef.current = JSON.stringify({
                        formData: {
                            name: '',
                            description: '',
                            duration_days: 30,
                            enrollment_method: 'manual',
                            auto_enrollment_rules: {
                                matching_logic: 'any',
                                lifecycle_stages: [],
                                roles: [],
                                company_wide: false
                            },
                            courseType: courseType
                        },
                        timelineItems: [],
                        messages: 1 // Initial welcome message
                    });
                    setHasUnsavedChanges(false);
                }, 100);
            }
        }
    }, [isOpen, courseType, initialCourseData]);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [isOpen]);

    // Drag handlers for resizable split panel
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;

        let newRatio = (mouseX / containerWidth) * 100;
        const minChatRatio = (MIN_CHAT_WIDTH / containerWidth) * 100;
        const maxChatRatio = 100 - (MIN_EDITOR_WIDTH / containerWidth) * 100;

        newRatio = Math.max(minChatRatio, Math.min(maxChatRatio, newRatio));
        setSplitRatio(newRatio);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem('ai_course_generator_split_ratio', splitRatio.toString());
        }
    }, [isDragging, splitRatio]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Send message with streaming
    const handleSendMessage = async (messageOverride = null) => {
        const messageToSend = messageOverride || inputMessage;
        if (!messageToSend.trim() || isGenerating || isStreaming) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: messageToSend.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsStreaming(true);
        setStreamingMessage('');
        setStreamingPhase('Connecting...');
        setSuggestions([]);

        try {
            // Check if user's message wants to use file content and files are pending
            const messageLower = messageToSend.toLowerCase();
            const wantsToUseFileContent = [
                'create', 'make', 'generate', 'summarize', 'analyze', 'based on',
                'from this', 'using this', 'quiz from', 'training from', 'extract',
                'what does', 'tell me about', 'explain', 'key points', 'course from'
            ].some(keyword => messageLower.includes(keyword));

            const pendingFiles = aiUploadedFiles.filter(f =>
                f.needs_intent || f.embedding_status === 'pending' || !f.embedding_status
            );

            // Auto-process pending files if user wants to use file content
            if (wantsToUseFileContent && pendingFiles.length > 0) {
                toast.loading('Processing files for AI analysis...', { id: 'auto-process' });

                // Add processing notification to chat
                const processingMsg = {
                    id: Date.now() + 0.5,
                    role: 'assistant',
                    content: `📄 Processing **${pendingFiles.map(f => f.file_name).join(', ')}** for AI analysis...`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, processingMsg]);

                // Process each pending file
                for (const file of pendingFiles) {
                    try {
                        const response = await api.post('/files/ai-upload/confirm-intent', {
                            file_key: file.file_key,
                            session_id: sessionId,
                            intent: 'reference',
                            file_buffer_base64: file.file_buffer_base64
                        });

                        if (response.data.success) {
                            setAiUploadedFiles(prev => prev.map(f =>
                                f.file_key === file.file_key
                                    ? {
                                        ...f,
                                        intent: 'reference',
                                        embedding_status: response.data.embedding_status,
                                        processing_status: response.data.embedding_status,
                                        document_id: response.data.document_id,
                                        needs_intent: false
                                    }
                                    : f
                            ));
                        }
                    } catch (error) {
                        console.error('Error auto-processing file:', error);
                    }
                }

                toast.success('✓ Analysis complete! Generating content...', { id: 'auto-process' });

                // Update the processing message to show completion
                setMessages(prev => prev.map(m =>
                    m.id === processingMsg.id
                        ? { ...m, content: `✓ Analyzed **${pendingFiles.map(f => f.file_name).join(', ')}**. Now generating content...` }
                        : m
                ));

                // Remove from pending intent files
                setPendingIntentFiles([]);
            }

            // Get updated file list after processing
            const currentFiles = aiUploadedFiles.map(f => ({
                file_name: f.file_name,
                file_key: f.file_key,
                file_url: f.file_url,
                file_size: f.file_size,
                file_type: f.file_type,
                document_id: f.document_id,
                embedding_status: f.embedding_status || 'completed'
            }));

            const token = localStorage.getItem('token');
            const conversationHistory = messages.map(m => ({
                role: m.role,
                content: m.content
            }));

            const response = await fetch(`${api.defaults.baseURL}/courses/ai/stream-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: messageToSend.trim(),
                    session_id: sessionId,
                    course_type: courseType,
                    conversation_history: conversationHistory,
                    current_course: formData.name ? formData : null,
                    timeline_items: timelineItems, // Send current items for AI context

                    web_search_enabled: isWebSearchEnabled,
                    uploaded_files: currentFiles
                })
            });

            if (!response.ok) {
                throw new Error('Failed to connect');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let fullContent = '';
            let buffer = '';
            let webSearchResults = null;
            let courseUpdate = null;
            let aiTimelineItems = null;
            let actionType = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            switch (data.type) {
                                case 'phase':
                                    setStreamingPhase(data.phase);
                                    break;
                                case 'web_search_results':
                                    webSearchResults = data.results;
                                    break;
                                case 'content':
                                    fullContent += data.content;
                                    // Don't show raw JSON in chat - only show for non-JSON streaming
                                    // Check if content looks like JSON (starts with { or [)
                                    if (!fullContent.trim().startsWith('{') && !fullContent.trim().startsWith('[')) {
                                        setStreamingMessage(fullContent);
                                    }
                                    break;
                                case 'complete':
                                    courseUpdate = data.course_update;
                                    aiTimelineItems = data.timeline_items;
                                    actionType = data.action;

                                    const assistantMessage = {
                                        id: Date.now(),
                                        role: 'assistant',
                                        content: data.reply || fullContent,
                                        timestamp: new Date(),
                                        action: actionType,
                                        webSearchResults: webSearchResults
                                    };
                                    setMessages(prev => [...prev, assistantMessage]);

                                    // Update course if AI provided updates
                                    if (courseUpdate && (actionType === 'generated' || actionType === 'updated')) {
                                        setFormData(prev => ({
                                            ...prev,
                                            name: courseUpdate.name || prev.name,
                                            description: courseUpdate.description || prev.description,
                                            duration_days: courseUpdate.duration_days || prev.duration_days,
                                            enrollment_method: courseUpdate.enrollment_method || prev.enrollment_method,
                                            auto_enrollment_rules: courseUpdate.auto_enrollment_rules || prev.auto_enrollment_rules
                                        }));
                                    }

                                    // Update timeline items if AI generated them
                                    if (aiTimelineItems && aiTimelineItems.length > 0) {
                                        if (actionType === 'updated' && timelineItems.length > 0) {
                                            // Merge AI items with existing items instead of replacing
                                            setTimelineItems(prev => {
                                                const mergedItems = [...prev];
                                                aiTimelineItems.forEach(aiItem => {
                                                    // Check if this item already exists (by matching title)
                                                    const existingIndex = mergedItems.findIndex(existing => {
                                                        const existingTitle = existing.task?.title || existing.notice?.title || existing.survey?.title || existing.name;
                                                        const aiTitle = aiItem.task?.title || aiItem.notice?.title || aiItem.survey?.title;
                                                        return existingTitle && aiTitle && existingTitle.toLowerCase() === aiTitle.toLowerCase();
                                                    });

                                                    if (existingIndex >= 0) {
                                                        // Update existing item (merge questions for surveys)
                                                        const existing = mergedItems[existingIndex];
                                                        if (existing.survey && aiItem.survey) {
                                                            // Merge survey questions
                                                            const existingQuestions = existing.survey.questions || [];
                                                            const newQuestions = aiItem.survey.questions || [];
                                                            mergedItems[existingIndex] = {
                                                                ...existing,
                                                                ...aiItem,
                                                                survey: {
                                                                    ...existing.survey,
                                                                    ...aiItem.survey,
                                                                    questions: [...existingQuestions, ...newQuestions]
                                                                }
                                                            };
                                                        } else {
                                                            // Replace with updated item
                                                            mergedItems[existingIndex] = { ...existing, ...aiItem };
                                                        }
                                                    } else {
                                                        // Add new item
                                                        mergedItems.push(aiItem);
                                                    }
                                                });
                                                return mergedItems;
                                            });
                                        } else {
                                            // Fresh generation - replace all
                                            setTimelineItems(aiTimelineItems);
                                        }
                                        // Auto-navigate to timeline step
                                        setActiveStep('timeline');
                                    }

                                    // Switch to editor panel on mobile after generation
                                    if ((courseUpdate || aiTimelineItems) && window.innerWidth < 1024) {
                                        setMobileActivePanel('editor');
                                    }

                                    // Set suggestions
                                    if (data.suggestions?.length > 0) {
                                        setSuggestions(data.suggestions);
                                    }
                                    break;
                                case 'error':
                                    throw new Error(data.message);
                                default:
                                    // Unknown event type, skip
                                    break;
                            }
                        } catch (parseErr) {
                            // Skip parse errors
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Stream error:', error);
            setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: `Sorry, there was an error: ${error.message}. Please try again.`,
                timestamp: new Date(),
                isError: true
            }]);
        } finally {
            setIsStreaming(false);
            setStreamingMessage('');
            setStreamingPhase(null);
        }
    };

    // Handle suggestion click
    const handleSuggestionClick = (suggestion) => {
        handleSendMessage(suggestion);
    };

    // Handle AI file uploads - DEFERRED PROCESSING
    const handleAiFilesUploaded = (newFiles) => {
        // Check if files need intent choice (deferred processing)
        const filesNeedingIntent = newFiles.filter(f => f.needs_intent && !f.error);

        if (filesNeedingIntent.length > 0) {
            // Store files awaiting intent choice
            setPendingIntentFiles(prev => [...prev, ...filesNeedingIntent]);
            // Add to uploaded files list
            setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

            // Add message with intent chooser
            const fileNames = filesNeedingIntent.map(f => f.file_name).join(', ');
            const intentMessage = {
                id: Date.now(),
                role: 'assistant',
                content: filesNeedingIntent.length === 1
                    ? `📄 Uploaded **"${fileNames}"**. What would you like to do with this file?`
                    : `📄 Uploaded ${filesNeedingIntent.length} files. What would you like to do with them?`,
                timestamp: new Date(),
                action: 'file_intent_choice',
                pendingFiles: filesNeedingIntent
            };
            setMessages(prev => [...prev, intentMessage]);
            return;
        }

        // Handle already-processed files (legacy flow)
        setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

        const completedFiles = newFiles.filter(f => f.embedding_status === 'completed');
        if (completedFiles.length > 0) {
            const fileNames = completedFiles.map(f => f.file_name).join(', ');
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📄 I've read **"${fileNames}"**. What would you like me to do with it?`,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create course content from this',
                'What are the key points?',
                'Generate training tasks from this'
            ]);
        }
    };

    // Direct file upload handler for inline file picker (Claude/Gemini style)
    const handleDirectFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file count
        if (aiUploadedFiles.length + files.length > 5) {
            toast.error('Maximum 5 files allowed per session');
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error(`Files exceed 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        setIsFileUploading(true);
        setFileUploadProgress(0);

        try {
            const formDataUpload = new FormData();
            files.forEach(file => {
                formDataUpload.append('files', file);
            });
            formDataUpload.append('session_id', sessionId);

            const response = await api.post('/files/ai-upload', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newFiles = response.data.files.filter(f => !f.error);
                handleAiFilesUploaded(newFiles);
                const fileNames = newFiles.map(f => f.file_name).join(', ');
                toast.success(
                    newFiles.length === 1
                        ? `Uploaded: ${fileNames}`
                        : `${newFiles.length} files uploaded`
                );
            }
        } catch (error) {
            console.error('AI file upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setIsFileUploading(false);
            setFileUploadProgress(0);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
        }
    };

    // Handle file processing complete callback
    const handleFileProcessingComplete = (completedFiles) => {
        if (completedFiles.length > 0) {
            setFileProcessingMessage(null);
            // Update file status in state
            setAiUploadedFiles(prev => prev.map(f => {
                const completed = completedFiles.find(cf => cf.file_key === f.file_key);
                if (completed) {
                    return { ...f, ...completed };
                }
                return f;
            }));
        }
    };

    // Handle file removal
    const handleRemoveAiFile = async (file) => {
        try {
            // If it was processed as reference, delete from embeddings
            if (file.document_id) {
                await api.delete(`/files/ai-reference/${file.document_id}`);
            }
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            toast.success('File removed');
        } catch (error) {
            console.error('Error removing file:', error);
            // Still remove from local state
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
        }
    };

    // Handle "Let AI Analyze" intent choice
    const handleLetAIAnalyze = async (files) => {
        setFileProcessingMessage('Processing documents for AI analysis...');

        try {
            let alreadyAnalyzedCount = 0;
            let newlyAnalyzedCount = 0;

            for (const file of files) {
                // Call confirm-intent endpoint with the file buffer
                const response = await api.post('/files/ai-upload/confirm-intent', {
                    file_key: file.file_key,
                    session_id: sessionId,
                    intent: 'reference',
                    file_buffer_base64: file.file_buffer_base64
                });

                if (response.data.success) {
                    // Check if file was already analyzed
                    if (response.data.already_analyzed) {
                        alreadyAnalyzedCount++;
                        toast.success(`📄 "${file.file_name}" was already analyzed`, { duration: 3000 });
                    } else {
                        newlyAnalyzedCount++;
                    }

                    // Update file status
                    setAiUploadedFiles(prev => prev.map(f =>
                        f.file_key === file.file_key
                            ? {
                                ...f,
                                intent: 'reference',
                                embedding_status: response.data.embedding_status,
                                processing_status: response.data.embedding_status,
                                document_id: response.data.document_id
                            }
                            : f
                    ));
                }
            }

            // Remove from pending files
            const fileKeys = files.map(f => f.file_key);
            setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

            // Add confirmation message based on analysis status
            const fileNames = files.map(f => f.file_name).join(', ');
            let messageContent;

            if (alreadyAnalyzedCount > 0 && newlyAnalyzedCount === 0) {
                messageContent = `📄 **${fileNames}** was already analyzed. I can answer questions about it, create course content, or summarize it.\n\nWhat would you like me to do?`;
            } else {
                messageContent = `🧠 I've analyzed **${fileNames}**. I can now answer questions about it, create course content based on its content, or summarize it.\n\nWhat would you like me to do?`;
            }

            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: messageContent,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create course content from this',
                'What are the key points?',
                'Generate training tasks from this'
            ]);

        } catch (error) {
            console.error('Error processing file for AI:', error);
            toast.error('Failed to process file for AI analysis');
        } finally {
            setFileProcessingMessage(null);
        }
    };

    // Save course
    const handleSaveCourse = async (saveStatus = 'published') => {
        if (!formData.name?.trim()) {
            toast.error('Please provide a course name');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                course: {
                    name: formData.name,
                    description: formData.description,
                    course_type: courseType,
                    duration_days: formData.duration_days,
                    enrollment_method: formData.enrollment_method,
                    status: saveStatus,
                    auto_enrollment_rules: formData.enrollment_method === 'automatic'
                        ? formData.auto_enrollment_rules
                        : null,
                    is_ai_generated: true,
                    chat_history: messages
                },
                timeline_items: timelineItems
            };

            if (initialCourseData?.id) {
                // Update existing course
                await api.put(`/courses/${initialCourseData.id}/with-tasks`, payload);
                toast.success('Course and timeline updated successfully!');
            } else {
                // Create new course
                if (timelineItems.length > 0) {
                    await api.post('/courses/with-tasks', payload);
                } else {
                    await api.post('/courses', payload);
                }

                const taskCount = timelineItems.filter(i => i.task || i.item_type === 'task').length;
                const noticeCount = timelineItems.filter(i => i.notice || i.item_type === 'notice').length;
                const surveyCount = timelineItems.filter(i => i.survey || i.item_type === 'survey').length;

                if (saveStatus === 'draft') {
                    toast.success('Course saved as draft!');
                } else {
                    toast.success(`Course created with ${taskCount} tasks, ${noticeCount} notices, ${surveyCount} surveys!`);
                }
            }

            onSuccess?.();
        } catch (error) {
            console.error('Error saving course:', error);
            toast.error(error.response?.data?.message || 'Failed to save course');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearChat = () => {
        if (window.confirm('Clear chat history? Your course editor content will be preserved.')) {
            setMessages([{
                id: Date.now(),
                role: 'assistant',
                content: "Chat cleared! Your course content is still in the editor. How can I help you further?",
                timestamp: new Date()
            }]);
        }
    };

    const handleClose = () => {
        if (hasUnsavedChanges && hasEditorContent()) {
            setShowUnsavedModal(true);
        } else {
            onClose();
        }
    };

    const handleSaveAsDraft = async () => {
        setShowUnsavedModal(false);
        await handleSaveCourse('draft');
        onClose();
    };

    const handleLeaveWithoutSaving = () => {
        setShowUnsavedModal(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div ref={containerRef} className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
            {/* Header - Always at top */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-transparent bg-gradient-to-r from-violet-600 to-purple-600">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="font-bold text-white text-lg">AI Course Assistant</h2>
                        <p className="text-xs text-white/80 font-medium capitalize">
                            {courseType === 'preboarding' ? 'Preboarding' : 'Onboarding'} Phase
                        </p>
                    </div>

                    {/* AI Chat Toggle — left side, with label */}
                    <button
                        onClick={toggleChatCollapsed}
                        className="hidden lg:flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full transition-all duration-200 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40"
                        title={isChatCollapsed ? 'Show AI Chat Panel' : 'Hide AI Chat Panel'}
                    >
                        {isChatCollapsed ? (
                            <>
                                <MessageSquare className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white whitespace-nowrap">Show AI Chat</span>
                            </>
                        ) : (
                            <>
                                <PanelLeftClose className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white/80 whitespace-nowrap">Hide Chat</span>
                            </>
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsPreviewOpen(true)}
                        disabled={!formData.name?.trim() && timelineItems.length === 0}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Preview how employees will see this course"
                    >
                        <Eye className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white hidden sm:inline">Preview</span>
                    </button>
                    <button
                        onClick={handleClearChat}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                        title="Clear chat"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleClose}
                        className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>
            </div>

            {/* Mobile Panel Toggle */}
            <div className="lg:hidden flex-shrink-0 flex bg-slate-800 border-b border-slate-700">
                <button
                    onClick={() => setMobileActivePanel('chat')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mobileActivePanel === 'chat'
                        ? 'bg-slate-700 text-white border-b-2 border-violet-500'
                        : 'text-slate-400 hover:text-white'
                        }`}
                >
                    <Sparkles className="w-4 h-4" />
                    AI Chat
                </button>
                <button
                    onClick={() => setMobileActivePanel('editor')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mobileActivePanel === 'editor'
                        ? 'bg-slate-700 text-white border-b-2 border-violet-500'
                        : 'text-slate-400 hover:text-white'
                        }`}
                >
                    <BookOpen className="w-4 h-4" />
                    Course Editor
                </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex overflow-hidden relative">

                {/* Collapsed Chat Sidebar Strip (desktop only) */}
                {isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center bg-slate-800 border-r border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors group"
                        style={{ width: '48px', minWidth: '48px' }}
                        onClick={toggleChatCollapsed}
                        title="Expand AI Chat"
                    >
                        <div className="flex flex-col items-center gap-3 py-5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20 group-hover:scale-110 transition-transform">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase" style={{ writingMode: 'vertical-lr' }}>AI Chat</span>
                            </div>
                        </div>
                        <div className="mt-auto mb-4 p-1.5 rounded-lg bg-slate-700/50 group-hover:bg-violet-600/30 transition-colors">
                            <PanelLeftOpen className="w-4 h-4 text-slate-400 group-hover:text-violet-300 transition-colors" />
                        </div>
                    </div>
                )}

                {/* Left Panel - AI Chat */}
                <div
                    className={`flex flex-col bg-slate-800 ${mobileActivePanel === 'chat' ? 'flex' : 'hidden lg:flex'}`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '0px' : `${splitRatio}%`)
                            : '100%',
                        minWidth: window.innerWidth >= 1024 && isChatCollapsed ? '0px' : undefined,
                        overflow: isChatCollapsed && window.innerWidth >= 1024 ? 'hidden' : undefined,
                        opacity: isChatCollapsed && window.innerWidth >= 1024 ? 0 : 1,
                        transition: 'width 300ms ease, opacity 200ms ease, min-width 300ms ease',
                        pointerEvents: isChatCollapsed && window.innerWidth >= 1024 ? 'none' : undefined
                    }}
                >
                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 md:px-5 md:py-6 space-y-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                            >
                                {message.role === 'user' ? (
                                    <div className="max-w-[85%]">
                                        <div className="bg-violet-600 rounded-2xl rounded-tr-none px-4 py-3 shadow-lg shadow-violet-900/20">
                                            <p className="text-sm text-white whitespace-pre-wrap">
                                                {message.content}
                                            </p>
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-1.5 text-right">
                                            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="bg-gradient-to-br from-violet-500 to-purple-600 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
                                            <Bot className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="max-w-[85%] space-y-3 p-4 bg-slate-700 rounded-2xl rounded-tl-none">
                                            <div className="space-y-3">
                                                {/* Action Cards */}
                                                {message.action && ['generated', 'updated', 'file_processed'].includes(message.action) && (
                                                    <div className="space-y-1.5">
                                                        {message.action === 'generated' && (
                                                            <div className="flex items-center gap-2">
                                                                <BookOpen className="w-4 h-4 text-slate-400" />
                                                                <span className="text-sm text-slate-400">Created</span>
                                                                <span className="text-sm text-slate-200 bg-slate-800 px-2.5 py-0.5 rounded border border-slate-700">
                                                                    {courseType}/{formData.name || 'New Course'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {message.action === 'updated' && (
                                                            <div className="flex items-center gap-2">
                                                                <BookOpen className="w-4 h-4 text-slate-400" />
                                                                <span className="text-sm text-slate-400">Updated</span>
                                                                <span className="text-sm text-slate-200 bg-slate-800 px-2.5 py-0.5 rounded border border-slate-700">
                                                                    {courseType}/{formData.name || 'Course'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {message.action === 'file_processed' && (
                                                            <div className="flex items-center gap-2">
                                                                <Brain className="w-4 h-4 text-violet-400" />
                                                                <span className="text-sm text-slate-400">Analyzed</span>
                                                                <span className="text-sm text-violet-300 bg-violet-500/10 px-2.5 py-0.5 rounded border border-violet-500/20">
                                                                    Document
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Message Content */}
                                                <div className="text-[15px] text-slate-200 leading-relaxed">
                                                    {message.content.split('\n').map((line, i) => (
                                                        <p key={i} className="mb-1.5 last:mb-0">
                                                            {line.startsWith('**') && line.endsWith('**') ? (
                                                                <strong className="text-white">{line.slice(2, -2)}</strong>
                                                            ) : line.startsWith('- ') ? (
                                                                <span className="flex gap-2 text-slate-300">
                                                                    <span className="text-primary">•</span>
                                                                    {line.slice(2)}
                                                                </span>
                                                            ) : line}
                                                        </p>
                                                    ))}
                                                </div>

                                                {/* Web Search Results */}
                                                {message.webSearchResults && message.webSearchResults.length > 0 && (
                                                    <WebSearchResultsDisplay results={message.webSearchResults} />
                                                )}

                                                {/* Intent Chooser Buttons */}
                                                {message.action === 'file_intent_choice' && message.pendingFiles && (
                                                    <div className="mt-3 pt-3 border-t border-slate-700/30">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={() => handleLetAIAnalyze(message.pendingFiles)}
                                                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg font-medium transition-all text-sm shadow-lg shadow-violet-500/20"
                                                            >
                                                                <Brain className="w-4 h-4" />
                                                                <span>Let AI Read & Analyze</span>
                                                            </button>
                                                        </div>
                                                        <p className="text-xs text-slate-500 mt-2">
                                                            AI will analyze the document to help generate course content
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Timestamp */}
                                                <p className="text-[11px] text-slate-500 mt-2">
                                                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}

                        {/* Streaming Message */}
                        {isStreaming && (
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-slate-700 text-slate-100">
                                    {streamingPhase && (
                                        <div className="flex items-center gap-2 text-xs text-violet-400 mb-2">
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            {streamingPhase}
                                        </div>
                                    )}
                                    {streamingMessage && (
                                        <div className="text-[15px] text-slate-200 leading-relaxed whitespace-pre-wrap">
                                            {streamingMessage}
                                            <span className="inline-block w-2 h-4 bg-violet-400 animate-pulse ml-0.5"></span>
                                        </div>
                                    )}
                                    {!streamingMessage && !streamingPhase && (
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                            <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                            <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div ref={chatEndRef} />
                    </div>

                    {/* Suggestions */}
                    {
                        suggestions.length > 0 && !isStreaming && (
                            <div className="px-4 pb-2">
                                <div className="flex items-center gap-2 mb-2">
                                    <Lightbulb className="w-4 h-4 text-amber-400" />
                                    <span className="text-xs text-slate-400">Suggestions</span>
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {suggestions.map((suggestion, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                            className="flex-shrink-0 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-full transition-colors whitespace-nowrap"
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )
                    }

                    {/* File Processing Indicator */}
                    {fileProcessingMessage && (
                        <div className="px-5 pb-2">
                            <div className="flex items-center gap-2 text-sm text-violet-400">
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                                <span>{fileProcessingMessage}</span>
                            </div>
                        </div>
                    )}

                    {/* Chat Input Area */}
                    <div className="p-4 border-t border-slate-700">
                        {/* Hidden File Input for Claude/Gemini-style inline file picker */}
                        <input
                            ref={aiFileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                            onChange={handleDirectFileUpload}
                            disabled={isGenerating || isStreaming || isFileUploading || aiUploadedFiles.length >= 5}
                            className="hidden"
                        />

                        {/* Inline File Upload Progress */}
                        {isFileUploading && (
                            <div className="mb-3 bg-slate-800/50 rounded-lg p-2.5">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Loader className="w-3.5 h-3.5 animate-spin text-violet-400" />
                                    <span className="text-xs text-slate-300">Uploading...</span>
                                    <span className="text-xs text-slate-500 ml-auto">{fileUploadProgress}%</span>
                                </div>
                                <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden">
                                    <div
                                        className="bg-gradient-to-r from-violet-500 to-purple-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${fileUploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Uploaded Files Display - Claude/Gemini style compact chips */}
                        {aiUploadedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {aiUploadedFiles.map((file) => {
                                    const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
                                    const isPdf = ext === 'PDF';
                                    const isDoc = ['DOC', 'DOCX'].includes(ext);
                                    const isImage = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(ext);
                                    const isSpreadsheet = ['XLS', 'XLSX'].includes(ext);

                                    return (
                                        <div
                                            key={file.file_key}
                                            className="group flex items-center gap-1.5 bg-slate-800/60 hover:bg-slate-800/80 border border-slate-700/50 rounded-md px-2 py-1 transition-colors"
                                        >
                                            {/* File Type Icon with color coding */}
                                            <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isPdf ? 'bg-red-500/20 text-red-400' :
                                                isDoc ? 'bg-blue-500/20 text-blue-400' :
                                                    isImage ? 'bg-green-500/20 text-green-400' :
                                                        isSpreadsheet ? 'bg-emerald-500/20 text-emerald-400' :
                                                            'bg-slate-600/50 text-slate-400'
                                                }`}>
                                                {isPdf ? 'PDF' : isDoc ? 'DOC' : isImage ? 'IMG' : isSpreadsheet ? 'XLS' : ext.slice(0, 3)}
                                            </div>
                                            <span className="text-xs text-slate-300 max-w-[120px] truncate">
                                                {file.file_name}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveAiFile(file)}
                                                className="p-0.5 text-slate-500 hover:text-red-400 rounded transition-colors opacity-60 group-hover:opacity-100"
                                                title="Remove file"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Input Wrapper */}
                        <div className="relative">
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Describe the course you want to create..."
                                rows={2}
                                className="w-full px-4 py-3 pr-24 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                            />

                            <div className="absolute right-2 bottom-2 flex gap-1">
                                <button
                                    onClick={() => aiFileInputRef.current?.click()}
                                    disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                                    className={`p-1.5 rounded-lg transition-colors ${aiUploadedFiles.length > 0
                                        ? 'bg-violet-600 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title="Attach files"
                                >
                                    <Paperclip className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={() => handleSendMessage()}
                                    disabled={!inputMessage.trim() || isGenerating}
                                    className="p-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
                                >
                                    {isGenerating || isStreaming ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Bottom Action Bar */}
                        <div className="flex items-center justify-between mt-2">
                            {/* <div className="flex items-center gap-1">
                                {/* Web Search Toggle */}
                            {/* <button
                                    onClick={() => setIsWebSearchEnabled(!isWebSearchEnabled)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-xs font-medium ${isWebSearchEnabled
                                        ? 'text-blue-400 bg-blue-500/10 border border-blue-500/20'
                                        : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 border border-transparent'
                                        }`}
                                    title={isWebSearchEnabled ? "Web Search Active" : "Enable Web Search"}
                                >
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>Web Search</span>
                                </button>
                            </div>  */}
                        </div>
                    </div>
                </div>

                {/* Draggable Divider - Desktop only */}
                {!isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center justify-center w-1 bg-slate-800 hover:bg-violet-600/20 cursor-col-resize transition-colors group z-20"
                        onMouseDown={handleMouseDown}
                    >
                        <div className="absolute flex flex-col items-center justify-center w-4 h-12 rounded bg-slate-700 group-hover:bg-violet-600 transition-colors shadow-lg border border-slate-600 group-hover:border-violet-500">
                            <GripVertical className="w-3 h-3 text-slate-400 group-hover:text-white" />
                        </div>
                    </div>
                )}

                {/* Right Panel - Course Editor */}
                <div
                    className={`flex flex-col bg-white overflow-hidden ${mobileActivePanel === 'editor' ? 'flex w-full' : 'hidden lg:flex'} relative transition-all duration-300`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '100%' : `${100 - splitRatio}%`)
                            : '100%',
                        flex: isChatCollapsed && window.innerWidth >= 1024 ? '1 1 0%' : undefined,
                        transition: 'width 300ms ease, flex 300ms ease'
                    }}
                >
                    {/* AI Loading Overlay */}
                    {(isGenerating || isStreaming) && (
                        <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center animate-in fade-in duration-300">
                            <div className="bg-white p-8 rounded-2xl shadow-xl border border-violet-100 flex flex-col items-center max-w-sm text-center">
                                <div className="relative w-16 h-16 mb-6">
                                    <div className="absolute inset-0 rounded-full border-4 border-violet-100"></div>
                                    <div className="absolute inset-0 rounded-full border-4 border-violet-500 border-t-transparent animate-spin"></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Sparkles className="w-6 h-6 text-violet-600 animate-pulse" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">
                                    {streamingPhase || 'Generating Course...'}
                                </h3>
                                <p className="text-gray-500 text-sm">
                                    AI is crafting your content based on your request.
                                </p>
                            </div>
                        </div>
                    )}

                    <CourseEditor
                        formData={formData}
                        setFormData={setFormData}
                        courseType={courseType}
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                        onSave={handleSaveCourse}
                        onCancel={handleClose}
                        isSaving={isSaving}
                        timelineItems={timelineItems}
                        setTimelineItems={setTimelineItems}
                    />
                </div>
            </div>

            {/* Unsaved Changes Modal */}
            {showUnsavedModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full mx-auto overflow-hidden ring-1 ring-black/5 transform transition-all scale-100 opacity-100">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-xl"></div>
                            <div className="relative flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shadow-inner">
                                    <AlertTriangle className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white tracking-tight">Unsaved Changes</h3>
                                    <p className="text-amber-100 font-medium text-sm">You have unsaved work in progress</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-8 py-8">
                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                You have modified this course but haven't saved your changes yet. If you leave now, your progress will be lost.
                            </p>

                            {/* Course Preview */}
                            {formData.name && (
                                <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group hover:border-violet-200 transition-colors">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gray-100 rounded-full -mr-12 -mt-12 opacity-50"></div>
                                    <div className="flex items-start gap-4 relative">
                                        <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600 shadow-sm group-hover:scale-110 transition-transform duration-300">
                                            <BookOpen className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-gray-900 truncate text-lg pr-4">{formData.name}</h4>
                                            <div className="flex items-center gap-3 mt-1.5">
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wide">
                                                    Course
                                                </span>
                                                {timelineItems.length > 0 && (
                                                    <span className="text-sm text-gray-500 font-medium">
                                                        {timelineItems.length} timeline {timelineItems.length === 1 ? 'item' : 'items'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!formData.name?.trim() && (
                                <div className="p-4 bg-amber-50 text-amber-800 rounded-xl border border-amber-100 text-sm font-medium flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    Course name is required to save as draft
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="px-8 py-6 bg-gray-50/80 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-4">
                            <button
                                onClick={handleLeaveWithoutSaving}
                                className="flex-1 px-6 py-3.5 text-gray-600 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 border border-gray-200 rounded-xl font-semibold transition-all duration-200 shadow-sm focus:ring-2 focus:ring-red-100 outline-none"
                            >
                                Leave Without Saving
                            </button>
                            <button
                                onClick={handleSaveAsDraft}
                                disabled={isSaving || !formData.name?.trim()}
                                className="flex-[1.5] px-6 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-violet-200 outline-none"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Saving Draft...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        Save Changes & Leave
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Course Preview Modal */}
            <AdminCoursePreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                courseData={formData}
                timelineItems={timelineItems}
            />
        </div>
    );
};

export default AICourseGenerator;
